import { useState, useEffect } from "react";
import { Activity, CheckCircle, AlertTriangle, XCircle, RefreshCw } from "lucide-react";
import { BACKEND_URL } from "../../context/AuthContext";

const STATUS_CONFIG = {
  healthy: { icon: CheckCircle, color: "text-green-400", bg: "bg-green-500/15", border: "border-green-500/20", label: "Healthy" },
  warning: { icon: AlertTriangle, color: "text-amber-400", bg: "bg-amber-500/15", border: "border-amber-500/20", label: "Warning" },
  offline: { icon: XCircle, color: "text-red-400", bg: "bg-red-500/15", border: "border-red-500/20", label: "Offline" },
};

const SERVICE_LABELS = {
  ai_provider: "AI Provider",
  voice_provider: "Voice Provider",
  queue: "Job Queue",
  storage: "Storage",
  database: "Database",
  api: "API Status",
};

export const AIHealth = ({ authToken }) => {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchHealth(); }, []);

  const fetchHealth = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/ai/health`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (res.ok) setHealth(await res.json());
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  if (loading) return <div className="text-center py-12"><div className="w-6 h-6 border-2 border-white/20 border-t-cyan-500 rounded-full animate-spin mx-auto" /></div>;

  const allHealthy = health && Object.values(health).every(v => v.status === "healthy");

  return (
    <div data-testid="ai-health">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-white flex items-center gap-2">
          <Activity className="w-5 h-5 text-cyan-400" /> AI Health
          {allHealthy && <span className="text-xs px-2 py-0.5 rounded-full bg-green-500/20 text-green-400 ml-2">All Systems Operational</span>}
        </h3>
        <button onClick={fetchHealth} className="text-white/40 hover:text-white transition p-2" data-testid="ai-health-refresh">
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {health && Object.entries(health).map(([key, val]) => {
          const cfg = STATUS_CONFIG[val.status] || STATUS_CONFIG.offline;
          const Icon = cfg.icon;
          return (
            <div key={key} className={`${cfg.bg} border ${cfg.border} rounded-2xl p-4`} data-testid={`health-${key}`}>
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-white font-semibold text-sm">{SERVICE_LABELS[key] || key}</h4>
                <Icon className={`w-5 h-5 ${cfg.color}`} />
              </div>
              <p className={`text-xs font-medium ${cfg.color} mb-1`}>{cfg.label}</p>
              <p className="text-white/40 text-[11px]">{val.detail}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
};
